import { Breadcrumb } from "antd";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import CustomMedia from "@/components/custom-media";
import PdfPreview from "@/components/pdf-preview";
import { tutorialsLearningRecordState } from "@/models/learning-record-model";
import { IAttachment, IConvertedType } from "@/types/communal";
import { TutorialTypeEnum } from "@/types/dept-tutorials";

import PictureCourseWare from "../picture-courseware";
import styles from "./index.module.less";
import { IDeptCourseContentProps } from "./props";

const DeptCourseContent: FC<IDeptCourseContentProps> = ({
  currentCourse,
  type,
  tutorial,
  gotoDeptCourse,
  reportTutorialsLearningRecord,
  getStartTime,
  index,
  isPreview = false,
}) => {
  const { t } = useTranslation();

  const [tutorialsLearningRecord, setTutorialsLearningRecord] = useRecoilState(
    tutorialsLearningRecordState
  );

  const crumbsData = [
    {
      title: (
        <span
          onClick={() => {
            gotoDeptCourse();
          }}
          className={styles["crumbs_title"]}
        >
          {t("dept_course")}
        </span>
      ),
    },
    {
      title: (
        <span className={styles["crumbs_title"]} title={currentCourse}>
          {currentCourse}
        </span>
      ),
    },
  ];

  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);

  const [currentTimbreIndex, setCurrentTimbreIndex] = useState<number>(0);

  const handleBackward = () => {
    const newIndex = currentAttachmentIndex - 1;

    if (newIndex >= 0) {
      setCurrentAttachmentIndex(newIndex);
    }
  };

  const handleForward = () => {
    const newIndex = currentAttachmentIndex + 1;

    if (newIndex < tutorial.attachments.length) {
      setCurrentAttachmentIndex(newIndex);
    }
  };

  // 处理媒体资源数据
  const convertedList = useMemo(() => {
    const handleAttachment = (type: number, attachment?: IAttachment) => {
      const converted = attachment?.converted;

      const res =
        type === 1
          ? converted
            ? Object.keys(converted).map((key) => {
                return {
                  key,
                  label: key,
                  src: converted[key as IConvertedType],
                };
              })
            : []
          : [
              {
                key: "audio",
                label: "audio",
                src: attachment?.url ?? "",
              },
            ];

      return res;
    };

    if (tutorial?.timbres?.length != 0) {
      if (tutorial.timbres[currentTimbreIndex].attachment) {
        return handleAttachment(
          type,
          tutorial.timbres[currentTimbreIndex].attachment
        );
      }
    }
    if (tutorial.attachments[currentAttachmentIndex]) {
      return handleAttachment(
        type,
        tutorial.attachments[currentAttachmentIndex]
      );
    }

    return [];
  }, [type, currentAttachmentIndex, tutorial, currentTimbreIndex]);

  // 默认播放资源url
  const defaultUrl = useMemo(() => {
    if (tutorial.timbres.length > 1) {
      // 存在ea的情况下
      return tutorial.timbres[currentTimbreIndex].attachment.url;
    } else {
      return tutorial.attachments[currentAttachmentIndex].url;
    }
  }, [currentAttachmentIndex, tutorial, currentTimbreIndex]);

  // 处理图片资源数据
  const imageData = useMemo(() => {
    if (type === 2) {
      return tutorial.attachments.map((item) => {
        return item.url;
      });
    }

    return [];
  }, [type, tutorial]);

  return (
    <div className={styles["main"]}>
      <div className={styles["top_area"]}>
        {isPreview || (
          <div className={styles["crumbs"]}>
            <Breadcrumb items={crumbsData} />
          </div>
        )}
        {(type === TutorialTypeEnum.Video ||
          type === TutorialTypeEnum.Audio) && (
          <div className={styles["player_area"]}>
            <div className={styles["player"]}>
              <CustomMedia
                convertedList={convertedList}
                eaTimbreList={tutorial.timbres}
                sourceType={type === 4 ? 2 : type}
                defaultSrc={defaultUrl}
                onPlay={() => getStartTime(new Date().toISOString())}
                onEnded={() => reportTutorialsLearningRecord()}
                onError={() => reportTutorialsLearningRecord()}
                onBackward={isPreview ? undefined : handleBackward}
                onForward={isPreview ? undefined : handleForward}
                onTimeUpdate={(e) => {
                  setTutorialsLearningRecord({
                    ...tutorialsLearningRecord,
                    // eslint-disable-next-line camelcase
                    viewed_time: Math.ceil(e.currentTime),
                  });
                }}
                onTimbreChange={(index) => setCurrentTimbreIndex(index)}
                allowEATimbre={tutorial.timbres.length > 1}
              />
            </div>
          </div>
        )}
        {type === TutorialTypeEnum.Image && (
          <div>
            <PictureCourseWare index={index} imageData={imageData} />
          </div>
        )}
        {type === TutorialTypeEnum.Document && (
          <div className={styles["pdf-container"]}>
            {tutorial.attachments[0].url && (
              <PdfPreview pdfUrl={tutorial.attachments[0].url} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeptCourseContent;
